import { useEffect, useState } from "react";
import { History, Loader2, Plus, RotateCcw, Save } from "lucide-react";
import { toast } from "sonner";
import { ComponentsEditor, CsvInput, Field, SectionEditor, inp } from "@/components/admin/SectionEditor";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from "@/components/ui/alert-dialog";
import { errMsg, useApi } from "@/lib/api";

const COMP_LABEL = { skda: "7 Kategori & alat sokongan", kbm: "Komponen KBM (3C, TSDR, 5 Dalil, LP → PG/TM, senarai semak)", adab: "Komponen ADAB" };

const HistoryList = ({ fid, versi, onReverted }) => {
  const api = useApi();
  const [items, setItems] = useState([]);
  const [confirm, setConfirm] = useState(null);
  useEffect(() => { api("get", `/admin/frameworks/${fid}/history`).then(setItems).catch(() => {}); }, [api, fid, versi]);
  const revert = async (v) => {
    try { onReverted(await api("post", `/admin/frameworks/${fid}/revert/${v}`)); toast.success(`Dipulihkan kepada versi ${v}`); }
    catch (e) { toast.error(errMsg(e)); }
  };
  return (
    <ul className="space-y-2" data-testid="fw-history">
      {items.map((h) => (
        <li key={h.versi} className="rounded-xl border border-line p-3 text-sm" data-testid={`fw-history-${h.versi}`}>
          <div className="space-y-1">
            <div className="font-mono-data text-xs break-all">v{h.versi} · {h.by} · {new Date(h.created_at).toLocaleString("ms-MY")}</div>
            {h.versi !== versi && <button onClick={() => setConfirm(h.versi)} data-testid={`fw-revert-${h.versi}`} className="text-xs text-q inline-flex items-center gap-1"><RotateCcw size={12} /> Kembali ke versi ini</button>}
          </div>
          <ul className="mt-1 text-mute text-xs list-disc pl-4">{h.perubahan.map((p, i) => <li key={i}>{p}</li>)}</ul>
        </li>
      ))}
      <AlertDialog open={!!confirm} onOpenChange={(o) => !o && setConfirm(null)}>
        <AlertDialogContent data-testid="fw-revert-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle>Kembali ke versi {confirm}?</AlertDialogTitle>
            <AlertDialogDescription>Definisi versi {confirm} akan disimpan sebagai versi baharu. Sejarah tidak dipadam.</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Batal</AlertDialogCancel>
            <AlertDialogAction onClick={() => { revert(confirm); setConfirm(null); }} data-testid="fw-revert-confirm">Pulihkan</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </ul>
  );
};

export const FrameworkEditor = ({ initial, onSaved }) => {
  const api = useApi();
  const [fw, setFw] = useState(initial);
  const [saving, setSaving] = useState(false);
  const [newIds, setNewIds] = useState([]);
  useEffect(() => { setFw(initial); setNewIds([]); }, [initial]);
  const set = (k, v) => setFw({ ...fw, [k]: v });
  const sections = [...fw.sections].sort((a, b) => a.order - b.order);
  const updateSec = (idx, s) => set("sections", fw.sections.map((x, j) => j === idx ? s : x));
  const addSec = () => {
    const id = `bahagian${fw.sections.length + 1}`;
    setNewIds([...newIds, id]);
    set("sections", [...fw.sections, { id, title: "", order: fw.sections.length + 1, purpose: "", instructions: "", model: "main", depends_on: [], output_fields: [], active: true }]);
  };
  const save = async () => {
    setSaving(true);
    try {
      const r = await api("put", `/admin/frameworks/${fw.id}`, fw);
      onSaved(r); setNewIds([]);
      toast.success(`Disimpan · prompt_versi ${r.prompt_versi}`);
    } catch (e) { toast.error(errMsg(e)); } finally { setSaving(false); }
  };
  return (
    <div className="grid lg:grid-cols-[1fr_320px] gap-8">
      <div className="space-y-6">
        <div className="grid sm:grid-cols-2 gap-4">
          <Field label="Nama kerangka"><input value={fw.nama} onChange={(e) => set("nama", e.target.value)} className={inp} data-testid="fw-nama" /></Field>
          <Field label="Penerangan ringkas"><input value={fw.ringkasan} onChange={(e) => set("ringkasan", e.target.value)} className={inp} data-testid="fw-ringkasan" /></Field>
        </div>
        {["prinsip", "aliran", "nota_nama"].filter((k) => fw[k] !== undefined && fw[k] !== null).map((k) => (
          <Field key={k} label={k.replace("_", " ")}><textarea rows={2} value={fw[k]} onChange={(e) => set(k, e.target.value)} className={inp} data-testid={`fw-${k}`} /></Field>
        ))}
        <div className="font-mono-data text-xs text-mute">Mod tweak: {fw.mode === "sequential" ? "berurutan (bahagian awal dikunci)" : "bebas (hanya bahagian itu berubah)"} · prompt_versi {fw.prompt_versi}</div>
        <div className="space-y-3">
          <div className="eyebrow">Langkah / tahap / bahagian</div>
          {sections.map((s) => {
            const idx = fw.sections.indexOf(s);
            return <SectionEditor key={idx} s={s} i={idx} fixed={fw.id === "hte"} isNew={newIds.includes(s.id) || !s.id} onChange={(v) => updateSec(idx, v)}
              onRemove={() => set("sections", fw.sections.filter((_, j) => j !== idx))} />;
          })}
          {fw.id !== "hte" && <button onClick={addSec} data-testid="fw-section-add" className="h-9 px-4 rounded-full border border-line text-sm inline-flex items-center gap-1.5"><Plus size={14} /> Tambah bahagian</button>}
        </div>
        {(fw.components?.length > 0 || COMP_LABEL[fw.id]) && (
          <div className="space-y-3"><div className="eyebrow">{COMP_LABEL[fw.id] || "Komponen"}</div>
            <ComponentsEditor items={fw.components || []} onChange={(v) => set("components", v)} /></div>
        )}
        {fw.lp_columns && (
          <Field label="Lajur LP (koma)"><CsvInput value={fw.lp_columns} onChange={(v) => set("lp_columns", v)} testid="fw-lp-columns" /></Field>
        )}
        <button onClick={save} disabled={saving} data-testid="fw-save-button"
          className="h-11 px-6 rounded-full bg-ink text-bg text-sm font-semibold inline-flex items-center gap-2 disabled:opacity-40">
          {saving ? <Loader2 size={15} className="animate-spin" /> : <Save size={15} />} Simpan kerangka
        </button>
      </div>
      <aside className="space-y-3">
        <div className="eyebrow inline-flex items-center gap-1.5"><History size={13} /> Sejarah perubahan</div>
        <HistoryList fid={fw.id} versi={fw.prompt_versi} onReverted={onSaved} />
      </aside>
    </div>
  );
};
